import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import pt from 'date-fns/locale/pt';
import de from 'date-fns/locale/de';
import { useService } from 'contexts/ServiceContextProvider';
import { useTranslation } from "react-i18next";
import "../../css/components/Calendar/VacationRequestModal.css";
import "../../css/main.css";

import { formattedHistory } from "./formatVacationHistory"
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import { DateTime } from 'luxon';


import { convertJSDateToUTCISO } from 'components/Custom/TimezoneConvertion.jsx';


/* eslint-disable react-hooks/exhaustive-deps */
import InfoIcon from '@mui/icons-material/Info';

import { useSidebar } from 'contexts/SidebarContextProvider';
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';

const VacationRequestModal = ({ open, handleCloseVacationRequestModal, myUserInMemberFormat, workspace_id, userTimezone }) => {
  const { t } = useTranslation();
  registerLocale('pt', pt);
  registerLocale('de', de);

  const [holidays, setHolidays] = useState([]); // State to store holidays
  const [overlappingHolidays, setOverlappingHolidays] = useState([]);

  const { submitNewVacation, getMyRemainingWorkspaceVacationDays } = useService();
  const { isSidebarOpen } = useSidebar();
  const [vacationDays, setVacationDays] = useState({});

  const { showSnackbar } = useSnackbar();



  useEffect(() => {
    async function fetchVacationDays() {
      if (workspace_id && open) {
        try {
          const response = await getMyRemainingWorkspaceVacationDays(workspace_id);
          setVacationDays(response.data.vacation_balance);
          setHolidays(response.data.holidays);
        } catch (error) {
          console.error('Failed to fetch vacation days:', error);
        }
      }
    }

    fetchVacationDays();
  }, [workspace_id, getMyRemainingWorkspaceVacationDays, open]);


  const [vacationData, setVacationData] = useState({
    title: '',
    description: '',
    type: 'RV',
    date_start: new Date(),
    date_end: new Date(),
    visibility: false,
    workspace_id: workspace_id || '',
    do_not_deduct: false,
  });

  const [error, setError] = useState('');
  const [totalDays, setTotalDays] = useState(0);
  const [totalWeekdays, setTotalWeekdays] = useState(0);
  const [showOverLimitModal, setShowOverLimitModal] = useState(false);

  useEffect(() => {
    if (myUserInMemberFormat && myUserInMemberFormat.full_name) {
      setVacationData((prevState) => ({
        ...prevState,
        title: `${t("calendar.vacation_person", { person: myUserInMemberFormat.full_name })}`,
      }));
    }
  }, [myUserInMemberFormat]);

  useEffect(() => {
    if (workspace_id) {
      setVacationData((prevState) => ({
        ...prevState,
        workspace_id: workspace_id,
      }));
    }
  }, [workspace_id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setVacationData({
      ...vacationData,
      [name]: value,
    });
  };

  const handleDateChange = (name, date) => {
    let newVacationData = { ...vacationData, [name]: date };

    const start = DateTime.fromJSDate(newVacationData.date_start).startOf('day');
    const end = DateTime.fromJSDate(newVacationData.date_end).startOf('day');

    if (name === 'date_start' && end < start) {
      setError(t("calendar.vacation_err1"));
    } else if (name === 'date_end' && end < start) {
      setError(t("calendar.vacation_err2"));
    } else {
      setError('');

      const diffDays = Math.ceil(end.diff(start, 'days').days + 1);
      setTotalDays(diffDays);

      const overlapping = holidays.filter(holiday => {
        const holidayDate = DateTime.fromISO(holiday.date).startOf('day');
        return holidayDate >= start && holidayDate <= end;
      });

      setOverlappingHolidays(overlapping);

      const weekdaysCount = getWeekdaysCount(start, end, overlapping);
      setTotalWeekdays(weekdaysCount);
    }

    setVacationData(newVacationData);
  };



  useEffect(() => {
    if (myUserInMemberFormat && myUserInMemberFormat.full_name) {
      let titleSuffix = t("calendar.vacation_suffix1"); // Default for RV which is the one we always start
      switch (vacationData.type) {
        case 'SL':
          titleSuffix = t("calendar.vacation_suffix2");
          break;
        case 'PL':
          titleSuffix = t("calendar.vacation_suffix3");
          break;
        case 'MPL':
          titleSuffix = t("calendar.vacation_suffix4");
          break;
        default:
          titleSuffix = t("calendar.vacation_suffix1");
      }

      setVacationData((prevState) => ({
        ...prevState,
        title: `${myUserInMemberFormat.full_name}${titleSuffix}`,
      }));
    }
  }, [myUserInMemberFormat, vacationData.type]);


  const getWeekdaysCount = (start, end, holidayList = []) => {
    let count = 0;
    let current = start.startOf('day');
    const endDay = end.startOf('day');

    const holidaySet = new Set(
      holidayList.map(h => DateTime.fromISO(h.date).startOf('day').toISODate())
    );

    while (current <= endDay) {
      const day = current.weekday; // Luxon: 1 = Monday, 7 = Sunday
      const isWeekend = day === 6 || day === 7;
      const isHoliday = holidaySet.has(current.toISODate());
      if (!isWeekend && !isHoliday) count++;
      current = current.plus({ days: 1 });
    }

    return count;
  };


  //When stuff is loaded, calculate week days/days affected
  useEffect(() => {
    const start = DateTime.fromJSDate(vacationData.date_start).startOf('day');
    const end = DateTime.fromJSDate(vacationData.date_end).startOf('day');

    const diffDays = Math.ceil(end.diff(start, 'days').days + 1);
    setTotalDays(diffDays);
    const overlapping = holidays.filter(holiday => {
      const holidayDate = DateTime.fromISO(holiday.date).startOf('day');
      return holidayDate >= start && holidayDate <= end;
    });
    setOverlappingHolidays(overlapping);

    const weekdaysCount = getWeekdaysCount(start, end, overlapping);
    setTotalWeekdays(weekdaysCount);
  }, [vacationData.date_start, vacationData.date_end, holidays]);


  const handleVisibilityChange = (event) => {
    setVacationData({
      ...vacationData,
      visibility: event.target.checked,
    });
  };

  const handleDeductabilityChange = (event) => {
    setVacationData({
      ...vacationData,
      do_not_deduct: event.target.checked,
    });
  };

  const handleSubmitConfirmed = () => {
    setShowOverLimitModal(false);
    const formattedData = {
      ...vacationData,
      date_start: convertJSDateToUTCISO(vacationData.date_start, userTimezone),
      date_end: convertJSDateToUTCISO(vacationData.date_end, userTimezone),
    };
    submitNewVacation(formattedData, workspace_id);
    showSnackbar(t("calendar.vacation_submitted"), 'info');
    handleClose();
  };

  const handleSubmit = () => {
    if (vacationData.date_start > vacationData.date_end) {
      setError(t("calendar.vacation_err1"));
      return;
    }

    if (!vacationData.do_not_deduct) {
      if (totalWeekdays > (vacationDays.total_available_days || 0)) {
        setShowOverLimitModal(true);
        return;
      }
    }

    const formattedData = {
      ...vacationData,
      date_start: convertJSDateToUTCISO(vacationData.date_start, userTimezone),
      date_end: convertJSDateToUTCISO(vacationData.date_end, userTimezone),
    };

    submitNewVacation(formattedData, workspace_id);
    showSnackbar(t("calendar.vacation_submitted"), 'info');
    handleClose();
  };

  const handleClose = () => {
    setVacationData({
      title: `${myUserInMemberFormat.full_name || ''}'s Vacation`,
      description: '',
      type: 'RV',
      date_start: new Date(),
      date_end: new Date(),
      visibility: false,
      workspace_id: workspace_id || '',
    });
    handleCloseVacationRequestModal();
  };




  return (
    <>
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="vacation-request-modal-title"
      aria-describedby="vacation-request-modal-description"
      className={`request-vacation-modal ${isSidebarOpen() ? 'sidebar-open' : ''}`}
    >
      <Box className="request-vacation-main-style">
        <Typography id="vacation-request-modal-title" variant="h6" component="h2" className="white-txt-color">
          {t("calendar.vacation_request")}
        </Typography>
        <TextField
          label={t("calendar.event_title")}
          name="title"
          value={vacationData.title}
          fullWidth
          margin="normal"
          variant="outlined"
          disabled
          className="request-vacation-disabled"
          InputLabelProps={{ className: "request-vacation-type-title" }}
          InputProps={{
            className: "request-vacation-type-select"
          }}
        />
        <TextField
          label={t("calendar.event_description")}
          name="description"
          value={vacationData.description}
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          rows={3}
          InputLabelProps={{ className: "request-vacation-type-title" }}
          InputProps={{
            className: "request-vacation-type-select",
          }}
          onChange={handleChange}
        />
        <FormControl margin="normal" className="request-vacation-form">
          <InputLabel className="request-vacation-type-title">{t("calendar.vacation_type")}</InputLabel>
          <Select
            label={t("calendar.vacation_type")}
            name="type"
            value={vacationData.type}
            onChange={handleChange}
            variant="outlined"
            className="request-vacation-type-select"
            MenuProps={{
              PaperProps: {
                className: "request-vacation-type-select",
              },
            }}
          >
            <MenuItem value="RV">{t("calendar.vacation_type1")}</MenuItem>
            <MenuItem value="SL">{t("calendar.vacation_type2")}</MenuItem>
            <MenuItem value="PL">{t("calendar.vacation_type3")}</MenuItem>
            <MenuItem value="MPL">{t("calendar.vacation_type4")}</MenuItem>
          </Select>
        </FormControl>
        <div className="request-vacation-date-info">
          <Box className="request-vacation-date-picker">
            <DatePicker
              selected={vacationData.date_start}
              onChange={(date) => handleDateChange('date_start', date)}
              dateFormat="yyyy-MM-dd"
              selectsStart
              startDate={vacationData.date_start}
              endDate={vacationData.date_end}
              customInput={
                <TextField
                  label={t("global.start_date")}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ className: "request-vacation-type-title" }}
                  InputProps={{
                    className: "request-vacation-type-select",
                  }}

                />
              }
              locale={t("global.short_lng")}

            />
            <DatePicker
              selected={vacationData.date_end}
              onChange={(date) => handleDateChange('date_end', date)}
              dateFormat="yyyy-MM-dd"
              selectsEnd
              startDate={vacationData.date_start}
              endDate={vacationData.date_end}
              minDate={vacationData.date_start}
              customInput={
                <TextField
                  label={t("calendar.event_end_date")}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ className: "request-vacation-type-title" }}
                  InputProps={{
                    className: "request-vacation-type-select",
                  }}
                />
              }
              locale={t("global.short_lng")}
            />
          </Box>
          <Box className="request-vacation-info-box">
            <Typography variant="body1" className="request-vacation-info-title">
              <strong>{t("calendar.vacation_info")}</strong>
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_per_year", { count: vacationDays.remaining_vacation_days })}
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_rollovers", { count: vacationDays.rollover_days })}
              {vacationDays.rollover_days > 0 && (
                <ScrollAwareTooltip
                  title={<span dangerouslySetInnerHTML={{ __html: formattedHistory(vacationDays.rollover_days_history, t) }} />}
                >
                  <InfoIcon className="white-txt-color" />
                </ScrollAwareTooltip>
              )}
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_used_days", { count: vacationDays.used_vacation_days })}
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_total_days", { count: vacationDays.total_available_days })}
            </Typography>
            {(vacationDays.pending_vacation_days || 0) > 0 && (
              <Typography variant="body2" className="request-vacation-type-title" style={{ color: '#f0a500' }}>
                {t("calendar.vacation_pending_days", { count: vacationDays.pending_vacation_days })}
              </Typography>
            )}
          </Box>
        </div>

        <div className="request-vacation-total-days">
          <Typography variant="subtitle1" className="request-vacation-total-count">
            {t("calendar.vacation_total_count", { count1: totalDays })}
          </Typography>
          <Typography variant="subtitle1">
            {t("calendar.vacation_total_count_weekdays", { count1: totalWeekdays })}
          </Typography>
          {overlappingHolidays.length > 0 && (
            <>
              <Typography variant="subtitle1" color={'lightblue'} className="holiday-overlap-info">
                {`${t("calendar.holiday_overlap", { count: overlappingHolidays.length })}`}
                <ScrollAwareTooltip
                  title={
                    <ul className="tooltip-list">
                      {overlappingHolidays.map((holiday) => (
                        <li key={holiday.date}>
                          {holiday.name} ({holiday.date})
                        </li>
                      ))}
                    </ul>
                  }
                  arrow
                >
                  <InfoIcon style={{ marginLeft: 8, cursor: "pointer" }} />
                </ScrollAwareTooltip>

              </Typography>
              <Typography variant="caption" color={'lightblue'} className="holiday-adjustment-info">
                {t("calendar.holiday_adjustment_info")}
              </Typography>
            </>
          )}
        </div>

        {error && (
          <Typography variant="body2" className="request-vacation-error">
            {error}
          </Typography>
        )}
        <Box className="request-vacation-flex">
          <FormControlLabel
            control={
              <Switch
                checked={vacationData.visibility}
                onChange={handleVisibilityChange}
                name="visibility"
                color="primary"
              />
            }
            label={t("calendar.event_public")}
            className="request-vacation-total-count"
          />
          <FormControlLabel
            control={
              <Switch
                checked={vacationData.do_not_deduct}
                onChange={handleDeductabilityChange}
                name="visibility"
                color="primary"
              />
            }
            label={t("calendar.vacation_no_deduct")}
            className="request-vacation-total-count"
          />
        </Box>
        {(!vacationData.visibility || vacationData.do_not_deduct) && (
          <Box
            className="request-vacation-admins-info-box"
            style={vacationData.visibility && vacationData.do_not_deduct ? { paddingTop: 0 } : {}}
          >
            {!vacationData.visibility && (
              <Typography variant="body2" className="request-vacation-type-title">
                {t("calendar.vacation_admins_info1")}
              </Typography>
            )}
            {vacationData.do_not_deduct && (
              <Typography variant="body2" className="request-vacation-total-count" style={vacationData.visibility ? { marginTop: 0 } : {}}>
                {t("calendar.vacation_admins_info2")}
              </Typography>
            )}
          </Box>
        )}
        <Box className="buttons-flex request-vacation-buttons-box">
          <Button
            variant="outlined"
            className="decline-button"
            onClick={handleClose}
          >
            <CancelIcon style={{ marginRight: 4 }} />
            <span className="button-text">
              {t("global.cancel")}
            </span>
          </Button>
          <Button
            variant="contained"
            className="accept-button"
            onClick={handleSubmit}
            disabled={!!error}
          >
            <CheckCircleIcon style={{ marginRight: 4 }} />
            <span className="button-text">
              {t("calendar.vacation_submit")}
            </span>
          </Button>
        </Box>
      </Box>
    </Modal>
    <Modal
        open={showOverLimitModal}
        onClose={() => setShowOverLimitModal(false)}
        aria-labelledby="over-limit-modal-title"
      >
        <Box className="request-vacation-main-style" style={{ width: 420 }}>
          <Typography id="over-limit-modal-title" variant="h6" component="h2" className="white-txt-color" style={{ marginBottom: 16 }}>
            {t("calendar.vacation_over_limit_title")}
          </Typography>
          <Typography variant="body2" className="request-vacation-type-title" style={{ marginBottom: 24 }}>
            {t("calendar.vacation_over_limit_body", {
              requested: totalWeekdays,
              available: vacationDays.total_available_days || 0,
              pending: vacationDays.pending_vacation_days || 0,
            })}
          </Typography>
          <Box className="buttons-flex request-vacation-buttons-box">
            <Button variant="outlined" className="decline-button" onClick={() => setShowOverLimitModal(false)}>
              <CancelIcon style={{ marginRight: 4 }} />
              <span className="button-text">{t("global.cancel")}</span>
            </Button>
            <Button variant="contained" className="accept-button" onClick={handleSubmitConfirmed}>
              <CheckCircleIcon style={{ marginRight: 4 }} />
              <span className="button-text">{t("calendar.vacation_submit")}</span>
            </Button>
          </Box>
        </Box>
      </Modal>
  </>
  );
};


export default VacationRequestModal;